import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  BookImage,
  Eye,
  EyeOff,
  ImagePlus,
  Images,
  Layers,
  LayoutTemplate,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react';
import type { AjusteImagem, Canto, ConfigCamadaImagem, EscopoImagem, FundoPronto, Lado } from '@miolos/core';
import {
  AJUSTES_IMAGEM,
  CANTOS,
  ESCOPOS_IMAGEM,
  LADOS,
  PREFIXO_ICONE,
  ROTULO_AJUSTE,
  ROTULO_CANTO,
  ROTULO_ESCOPO,
  ROTULO_LADO,
  ROTULO_PAPEL_IMAGEM,
  copiarParaBorda,
  copiarParaCanto,
  criarCamadaPadrao,
  criarCamadaDeCanto,
  medirPagina,
  proximaOrdem,
  type IconeBiblioteca,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Campo } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/switch';
import { api, urlDaImagem, type ArquivoEnviado } from '@/lib/api';
import { LinhaSwitch } from '@/components/ui/switch';
import { useAuth } from '@/auth/AuthContext';
import { formatarBytes } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { useEditor } from '../EstadoEditor';
import { CamadaEditor } from '../CamadaEditor';
import { SeletorDeIcones } from './SeletorDeIcones';
import { TituloPasso } from '../ui';

/** Lê a proporção original da imagem para não distorcer nas cópias. */
function medirImagem(url: string): Promise<number | null> {
  return new Promise((resolver) => {
    const imagem = new Image();
    imagem.onload = () => resolver(imagem.naturalHeight > 0 ? imagem.naturalWidth / imagem.naturalHeight : null);
    imagem.onerror = () => resolver(null);
    imagem.src = url;
  });
}

export function PassoImagens() {
  const { config, atualizar, paleta } = useEditor();
  const { usuario } = useAuth();
  const idDoUsuario = usuario?.id ?? '';

  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [biblioteca, setBiblioteca] = useState<ArquivoEnviado[]>([]);
  const [mostrarBiblioteca, setMostrarBiblioteca] = useState(false);
  const [mostrarIcones, setMostrarIcones] = useState(false);
  const entradaArquivo = useRef<HTMLInputElement>(null);
  /** Upload de arte final: capa e contracapa prontas. */
  const entradaCapa = useRef<HTMLInputElement>(null);
  const entradaContracapa = useRef<HTMLInputElement>(null);
  const [enviandoCapa, setEnviandoCapa] = useState<'frente' | 'verso' | null>(null);
  /** Upload de fundo pronto (meu modelo de página). */
  const entradaFundo = useRef<HTMLInputElement>(null);
  const [enviandoFundo, setEnviandoFundo] = useState(false);
  const [mostrarModelos, setMostrarModelos] = useState(false);

  const fundos = config.fundos;

  const camadas = config.camadasImagem;
  const medidas = medirPagina(config.formato);

  const ordenadas = useMemo(
    () => [...camadas].sort((a, b) => a.ordem - b.ordem || a.id.localeCompare(b.id)),
    [camadas],
  );

  const camadaSelecionada = ordenadas.find((camada) => camada.id === selecionada) ?? ordenadas[0] ?? null;

  useEffect(() => {
    if (!idDoUsuario) return;
    api
      .listarArquivos()
      .then((resposta) => setBiblioteca(resposta.arquivos))
      .catch(() => undefined);
  }, [idDoUsuario, enviando]);

  const adicionarCamada = useCallback(
    async (url: string, nome: string, modo: 'PAGINA_INTEIRA' | 'CANTO' = 'PAGINA_INTEIRA') => {
      const proporcao = await medirImagem(urlDaImagem(url));
      const entrada = { url, nome, proporcao };

      const nova =
        modo === 'CANTO'
          ? criarCamadaDeCanto(entrada, config.formato, 'SUP_ESQ', proximaOrdem(camadas))
          : criarCamadaPadrao(entrada, config.formato, proximaOrdem(camadas));

      if (modo === 'PAGINA_INTEIRA') {
        nova.larguraMm = medidas.larguraMm;
        nova.alturaMm = medidas.alturaMm;
      }

      atualizar({ camadasImagem: [...camadas, nova] });
      setSelecionada(nova.id);
    },
    [camadas, config.formato, medidas.alturaMm, medidas.larguraMm, atualizar],
  );

  /** Coloca um ícone da biblioteca no miolo, como uma camada livre. */
  const adicionarIcone = useCallback(
    (icone: IconeBiblioteca) => {
      const nova = criarCamadaPadrao(
        { url: `${PREFIXO_ICONE}${icone.id}`, nome: icone.nome, proporcao: 1 },
        config.formato,
        proximaOrdem(camadas),
      );
      nova.papel = 'LIVRE';
      nova.larguraMm = 18;
      nova.alturaMm = 18;
      nova.proporcional = false;
      nova.cor = paleta.destaque;
      nova.escopo = 'CONTEUDO';

      atualizar({ camadasImagem: [...camadas, nova] });
      setSelecionada(nova.id);
      setMostrarIcones(false);
    },
    [camadas, config.formato, atualizar, paleta.destaque],
  );

  const enviarArquivo = useCallback(
    async (arquivo: File) => {
      setErro(null);
      setEnviando(true);
      try {
        const resposta = await api.enviarImagem(arquivo);
        await adicionarCamada(resposta.arquivo.url, resposta.arquivo.nome);
        setMostrarBiblioteca(false);
      } catch (falha) {
        setErro(falha instanceof Error ? falha.message : 'Não foi possível enviar a imagem.');
      } finally {
        setEnviando(false);
      }
    },
    [adicionarCamada],
  );

  /**
   * Sobe a arte final de capa ou contracapa.
   *
   * É o caminho de quem vende o arquivo pronto: a imagem entra como a página
   * inteira, sem a capa desenhada por cima.
   */
  const enviarArteDeCapa = useCallback(
    async (onde: 'frente' | 'verso', arquivo: File) => {
      setErro(null);
      setEnviandoCapa(onde);
      try {
        const resposta = await api.enviarImagem(arquivo);
        atualizar({
          capas: { ...config.capas, [onde]: resposta.arquivo.url },
          // Enviar a arte liga a capa: ninguém quer a imagem guardada sem sair.
          ...(onde === 'frente' ? { capa: true } : {}),
        });
      } catch (falha) {
        setErro(falha instanceof Error ? falha.message : 'Não foi possível enviar a arte.');
      } finally {
        setEnviandoCapa(null);
      }
    },
    [atualizar, config.capas],
  );

  const removerArteDeCapa = (onde: 'frente' | 'verso') => {
    atualizar({
      capas: { ...config.capas, [onde]: null },
      /*
       * Tirar a arte leva a capa gerada junto.
       *
       * Quem enviou a arte não pediu o desenho do sistema: sem esta linha, apagar a
       * imagem faria aparecer uma capa que ninguém escolheu.
       */
      ...(onde === 'frente' ? { capa: false } : {}),
    });
  };

  /** Novo fundo pronto: entra nas páginas de dia, com a arte cheia. */
  const adicionarFundo = useCallback(
    (url: string, nome: string) => {
      const novo: FundoPronto = {
        id: `fundo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
        url,
        nome,
        destino: 'DIAS',
        opacidade: 1,
        veu: 0,
        ajuste: 'COBRIR',
        espelharPares: false,
        visivel: true,
      };
      atualizar({ fundos: [...fundos, novo] });
    },
    [atualizar, fundos],
  );

  const enviarFundo = useCallback(
    async (arquivo: File) => {
      setErro(null);
      setEnviandoFundo(true);
      try {
        const resposta = await api.enviarImagem(arquivo);
        adicionarFundo(resposta.arquivo.url, resposta.arquivo.nome);
      } catch (falha) {
        setErro(falha instanceof Error ? falha.message : 'Não foi possível enviar o modelo.');
      } finally {
        setEnviandoFundo(false);
      }
    },
    [adicionarFundo],
  );

  const atualizarFundo = (id: string, alteracoes: Partial<FundoPronto>) => {
    atualizar({ fundos: fundos.map((fundo) => (fundo.id === id ? { ...fundo, ...alteracoes } : fundo)) });
  };

  const removerFundo = (id: string) => {
    atualizar({ fundos: fundos.filter((fundo) => fundo.id !== id) });
  };

  const atualizarCamada = useCallback(
    (id: string, alteracoes: Partial<ConfigCamadaImagem>) => {
      atualizar({
        camadasImagem: camadas.map((camada) => (camada.id === id ? { ...camada, ...alteracoes } : camada)),
      });
    },
    [camadas, atualizar],
  );

  const removerCamada = useCallback(
    (id: string) => {
      atualizar({ camadasImagem: camadas.filter((camada) => camada.id !== id) });
      if (selecionada === id) setSelecionada(null);
    },
    [camadas, atualizar, selecionada],
  );

  const duplicarCamada = useCallback(
    (id: string) => {
      const original = camadas.find((camada) => camada.id === id);
      if (!original) return;
      const copia: ConfigCamadaImagem = {
        ...original,
        id: `camada-${Math.random().toString(36).slice(2, 10)}`,
        ordem: proximaOrdem(camadas),
        xMm: Math.round((original.xMm + 5) * 10) / 10,
        yMm: Math.round((original.yMm + 5) * 10) / 10,
      };
      atualizar({ camadasImagem: [...camadas, copia] });
      setSelecionada(copia.id);
    },
    [camadas, atualizar],
  );

  const trazerParaFrente = useCallback(
    (id: string) => atualizarCamada(id, { ordem: proximaOrdem(camadas) }),
    [atualizarCamada, camadas],
  );

  const moverNaPilha = useCallback(
    (id: string, direcao: -1 | 1) => {
      const atual = ordenadas.findIndex((camada) => camada.id === id);
      const destino = atual + direcao;
      if (atual < 0 || destino < 0 || destino >= ordenadas.length) return;

      const a = ordenadas[atual]!;
      const b = ordenadas[destino]!;
      atualizar({
        camadasImagem: camadas.map((camada) => {
          if (camada.id === a.id) return { ...camada, ordem: b.ordem };
          if (camada.id === b.id) return { ...camada, ordem: a.ordem };
          return camada;
        }),
      });
    },
    [ordenadas, camadas, atualizar],
  );

  const alternarVisibilidade = useCallback(
    (id: string) => {
      const camada = camadas.find((item) => item.id === id);
      if (camada) atualizarCamada(id, { visivel: !camada.visivel });
    },
    [camadas, atualizarCamada],
  );

  /** Replica a camada nos outros cantos ou nas outras bordas. */
  const replicar = useCallback(
    (id: string, tipo: 'CANTOS' | 'BORDAS') => {
      const original = camadas.find((camada) => camada.id === id);
      if (!original) return;

      let ordem = proximaOrdem(camadas);
      const novas: ConfigCamadaImagem[] = [];

      if (tipo === 'CANTOS') {
        const cantoAtual: Canto = original.papel === 'CANTO' ? original.canto : 'SUP_ESQ';
        for (const canto of CANTOS) {
          if (canto === cantoAtual) continue;
          novas.push(copiarParaCanto(original, canto, ordem));
          ordem += 1;
        }
      } else {
        const ladoAtual: Lado | null = original.papel === 'BORDA' ? original.lado : null;
        for (const lado of LADOS) {
          if (lado === ladoAtual) continue;
          novas.push(copiarParaBorda(original, lado, medidas, ordem));
          ordem += 1;
        }
      }

      atualizar({ camadasImagem: [...camadas, ...novas] });
      setSelecionada(novas[0]?.id ?? id);
    },
    [camadas, medidas, atualizar],
  );

  const quadroCompleto = useCallback(() => {
    const base = ordenadas[0];
    if (base) replicar(base.id, 'CANTOS');
  }, [ordenadas, replicar]);

  return (
    <div className="space-y-6">
      <TituloPasso
        titulo="Camadas de imagem"
        descricao="Você pode usar quantas imagens quiser no miolo: fundos de página inteira, faixas nas bordas, figuras nos cantos ou elementos soltos em qualquer posição. Cada camada tem posição, tamanho, rotação e regra de distribuição entre as páginas."
      />

      {/* Capas prontas: capa e contracapa enviadas */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="space-y-1">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <BookImage className="size-4 text-primary" />
            Capas prontas (capa e contracapa)
          </h3>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Envie a arte final da <strong className="text-foreground">capa</strong> e da{' '}
            <strong className="text-foreground">contracapa</strong> para vender o arquivo pronto. A imagem entra como
            página inteira, de borda a borda, e substitui a capa desenhada pelo sistema — nada de texto ou ornamento
            por cima. Envie no tamanho do papel ({medidas.larguraMm} × {medidas.alturaMm} mm) para sair sem corte.
          </p>
        </div>

        <input
          ref={entradaCapa}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(evento) => {
            const arquivo = evento.target.files?.[0];
            if (arquivo) void enviarArteDeCapa('frente', arquivo);
            evento.target.value = '';
          }}
        />
        <input
          ref={entradaContracapa}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(evento) => {
            const arquivo = evento.target.files?.[0];
            if (arquivo) void enviarArteDeCapa('verso', arquivo);
            evento.target.value = '';
          }}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          {(
            [
              { onde: 'frente' as const, rotulo: 'Capa', dica: 'Frente do arquivo, primeira página.' },
              { onde: 'verso' as const, rotulo: 'Contracapa', dica: 'Última página do miolo.' },
            ]
          ).map(({ onde, rotulo, dica }) => {
            const url = config.capas[onde];
            const enviandoEste = enviandoCapa === onde;
            return (
              <div key={onde} className="space-y-2 rounded-md border border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold">{rotulo}</span>
                  {url ? (
                    <Badge tom="sucesso">
                      com arte ({medidas.larguraMm} × {medidas.alturaMm} mm)
                    </Badge>
                  ) : (
                    <Badge tom="neutro">sem arte</Badge>
                  )}
                </div>

                {url ? (
                  <img
                    src={urlDaImagem(url)}
                    alt={rotulo}
                    className="h-24 w-full rounded object-cover ring-1 ring-border"
                  />
                ) : (
                  <div className="flex h-24 w-full items-center justify-center rounded border border-dashed border-border text-[11px] text-muted-foreground">
                    {onde === 'frente' ? 'Capa gerada pelo sistema' : 'Sem contracapa'}
                  </div>
                )}

                <p className="text-[11px] leading-relaxed text-muted-foreground">{dica}</p>

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="contorno"
                    tamanho="pequeno"
                    disabled={!usuario || enviandoEste}
                    onClick={() => (onde === 'frente' ? entradaCapa : entradaContracapa).current?.click()}
                  >
                    {enviandoEste ? <Loader2 className="animate-spin" /> : <ImagePlus />}
                    {url ? 'Trocar arte' : `Enviar ${rotulo.toLowerCase()}`}
                  </Button>
                  {url ? (
                    <Button tamanho="pequeno" variant="fantasma" onClick={() => removerArteDeCapa(onde)}>
                      <Trash2 />
                      Remover
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
          <LinhaSwitch
            id="capa-ativa"
            rotulo="Incluir capa no miolo"
            descricao="Desligue para gerar o arquivo sem capa nenhuma (só as páginas internas)."
            checked={config.capa}
            onCheckedChange={(valor) => atualizar({ capa: valor })}
          />
          <LinhaSwitch
            id="contracapa-ativa"
            rotulo="Incluir contracapa"
            descricao="Fecha o miolo com a contracapa. Sem arte enviada, sai uma página em branco para colar a sua."
            checked={config.capas.incluirContracapa || Boolean(config.capas.verso)}
            onCheckedChange={(valor) => atualizar({ capas: { ...config.capas, incluirContracapa: valor } })}
          />
        </div>

        {erro ? (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
            {erro}
          </p>
        ) : null}
      </section>

      {/* Fundos prontos: meus modelos de página */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="space-y-1">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <LayoutTemplate className="size-4 text-primary" />
            Fundos prontos (meus modelos de página)
          </h3>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Envie o seu modelo de página — papel timbrado, moldura, campos, linhas — e escolha em que páginas ele
            entra. O sistema continua imprimindo por cima a <strong className="text-foreground">data</strong>, o{' '}
            <strong className="text-foreground">dia da semana</strong>, o título e o conteúdo do dia, então a
            diagramação sai pronta. Tamanho ideal: {medidas.larguraMm} × {medidas.alturaMm} mm (a folha inteira).
          </p>
        </div>

        <input
          ref={entradaFundo}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(evento) => {
            const arquivo = evento.target.files?.[0];
            if (arquivo) void enviarFundo(arquivo);
            evento.target.value = '';
          }}
        />

        <div className="flex flex-wrap items-center gap-3">
          <Button variant="contorno" disabled={!usuario || enviandoFundo} onClick={() => entradaFundo.current?.click()}>
            {enviandoFundo ? <Loader2 className="animate-spin" /> : <ImagePlus />}
            {enviandoFundo ? 'Enviando...' : 'Enviar meu modelo de página'}
          </Button>
          {fundos.length > 0 ? <Badge tom="principal">{fundos.length} fundo(s)</Badge> : null}
          {biblioteca.length > 0 ? (
            <Button
              variant="fantasma"
              tamanho="pequeno"
              disabled={!usuario}
              onClick={() => setMostrarModelos((atual) => !atual)}
            >
              <Images />
              {mostrarModelos ? 'Esconder imagens enviadas' : 'Usar imagem já enviada'}
            </Button>
          ) : null}
        </div>

        {mostrarModelos ? (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {biblioteca.map((arquivo) => (
              <button
                key={arquivo.id}
                type="button"
                onClick={() => {
                  adicionarFundo(arquivo.url, arquivo.nome);
                  setMostrarModelos(false);
                }}
                className="group overflow-hidden rounded border border-border bg-background"
              >
                <img
                  src={urlDaImagem(arquivo.url)}
                  alt={arquivo.nome}
                  className="h-16 w-full object-cover transition-transform group-hover:scale-105"
                />
                <span className="block truncate px-1 py-0.5 text-[10px] text-muted-foreground">{arquivo.nome}</span>
              </button>
            ))}
          </div>
        ) : null}

        {fundos.length === 0 ? (
          <p className="rounded-md border border-dashed border-border p-3 text-[11px] leading-relaxed text-muted-foreground">
            Sem fundo enviado, o miolo continua exatamente como o sistema gera hoje.
          </p>
        ) : (
          <ul className="space-y-3">
            {fundos.map((fundo) => (
              <li key={fundo.id} className="space-y-3 rounded-md border border-border p-3">
                <div className="flex items-start gap-3">
                  <img
                    src={urlDaImagem(fundo.url)}
                    alt={fundo.nome}
                    className="h-20 w-16 shrink-0 rounded object-cover ring-1 ring-border"
                  />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-xs font-semibold">{fundo.nome || 'Meu modelo'}</span>
                      <Badge tom={fundo.visivel ? 'sucesso' : 'neutro'}>
                        {fundo.visivel ? 'em uso' : 'desligado'}
                      </Badge>
                    </div>

                    <Campo rotulo="Onde entra este fundo" dica="A data e o conteúdo são impressos por cima da arte.">
                      <Select
                        value={fundo.destino}
                        onValueChange={(valor) => atualizarFundo(fundo.id, { destino: valor as EscopoImagem })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ESCOPOS_IMAGEM.map((escopo) => (
                            <SelectItem key={escopo} value={escopo}>
                              {ROTULO_ESCOPO[escopo]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Campo>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="font-medium">Opacidade</span>
                      <span className="font-mono text-muted-foreground">
                        {Math.round(fundo.opacidade * 100)}%
                      </span>
                    </div>
                    <Slider
                      value={[fundo.opacidade]}
                      min={0.1}
                      max={1}
                      step={0.05}
                      onValueChange={([valor]) => atualizarFundo(fundo.id, { opacidade: valor ?? 1 })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="font-medium">Véu (legibilidade)</span>
                      <span className="font-mono text-muted-foreground">{Math.round(fundo.veu * 100)}%</span>
                    </div>
                    <Slider
                      value={[fundo.veu]}
                      min={0}
                      max={0.9}
                      step={0.05}
                      onValueChange={([valor]) => atualizarFundo(fundo.id, { veu: valor ?? 0 })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="font-medium">Encaixe da arte</span>
                    </div>
                    <Select
                      value={fundo.ajuste}
                      onValueChange={(valor) => atualizarFundo(fundo.id, { ajuste: valor as AjusteImagem })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AJUSTES_IMAGEM.map((ajuste) => (
                          <SelectItem key={ajuste} value={ajuste}>
                            {ROTULO_AJUSTE[ajuste]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <LinhaSwitch
                    id={`fundo-espelhar-${fundo.id}`}
                    rotulo="Espelhar nas páginas pares"
                    descricao="Para arte com margem de lombada."
                    checked={fundo.espelharPares}
                    onCheckedChange={(valor) => atualizarFundo(fundo.id, { espelharPares: valor })}
                  />
                  <LinhaSwitch
                    id={`fundo-visivel-${fundo.id}`}
                    rotulo="Em uso"
                    descricao=""
                    checked={fundo.visivel}
                    onCheckedChange={(valor) => atualizarFundo(fundo.id, { visivel: valor })}
                  />
                  <Button
                    variant="fantasma"
                    tamanho="pequeno"
                    className="ml-auto text-destructive"
                    onClick={() => removerFundo(fundo.id)}
                  >
                    <Trash2 />
                    Remover
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Upload e biblioteca */}
      <section className="space-y-3 rounded-lg border border-dashed border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={entradaArquivo}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(evento) => {
              const arquivo = evento.target.files?.[0];
              if (arquivo) void enviarArquivo(arquivo);
              evento.target.value = '';
            }}
          />
          <Button
            variant="contorno"
            disabled={!usuario || enviando}
            onClick={() => entradaArquivo.current?.click()}
          >
            {enviando ? <Loader2 className="animate-spin" /> : <ImagePlus />}
            {enviando ? 'Enviando...' : 'Enviar nova imagem'}
          </Button>

          <Button
            variant="contorno"
            disabled={!usuario || biblioteca.length === 0}
            onClick={() => setMostrarBiblioteca(true)}
          >
            <Images />
            Minhas imagens ({biblioteca.length})
          </Button>

          <Button variant="contorno" onClick={() => setMostrarIcones(true)}>
            <Sparkles />
            Biblioteca de ícones
          </Button>

          {ordenadas.length > 0 ? (
            <Button variant="fantasma" onClick={quadroCompleto}>
              <Sparkles />
              Replicar a primeira nos 4 cantos
            </Button>
          ) : null}

          <span className="text-xs text-muted-foreground">PNG, JPG ou WEBP até 6 MB por imagem.</span>
        </div>

        {!usuario ? (
          <p className="rounded-md border border-amber-300/70 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
            Entre na sua conta para enviar imagens — elas ficam salvas na sua área e podem ser reaproveitadas em
            outros miolos.
          </p>
        ) : null}

        {erro ? <p className="text-xs text-destructive">{erro}</p> : null}
      </section>

      {camadas.length === 0 ? (
        <div className="rounded-lg border border-border bg-muted/40 p-8 text-center">
          <Layers className="mx-auto size-6 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">Nenhuma imagem no miolo ainda</p>
          <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
            Envie uma imagem ou escolha um ícone da biblioteca. Depois use os botões de posição e os atalhos de
            replicação para montar molduras de 4 cantos, faixas de borda ou um rodízio de páginas inteiras.
          </p>
          <Button variant="contorno" className="mx-auto mt-4" onClick={() => setMostrarIcones(true)}>
            <Sparkles />
            Escolher um ícone
          </Button>
        </div>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
          {/* Lista de camadas */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Camadas ({camadas.length})</h3>
              <Badge>{medidas.larguraMm} × {medidas.alturaMm} mm</Badge>
            </div>

            <ul className="space-y-2">
              {[...ordenadas].reverse().map((camada, indiceInvertido) => {
                const posicao = ordenadas.length - 1 - indiceInvertido;
                const ativa = camadaSelecionada?.id === camada.id;
                return (
                  <li key={camada.id}>
                    <div
                      className={cn(
                        'flex items-stretch gap-2 rounded-lg border p-2 transition-colors',
                        ativa ? 'border-primary bg-primary/[0.04]' : 'border-border bg-card hover:border-primary/40',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => setSelecionada(camada.id)}
                        className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                      >
                        <span className="size-11 shrink-0 overflow-hidden rounded border border-border bg-muted">
                          <img
                            src={urlDaImagem(camada.url)}
                            alt=""
                            className="size-full object-cover"
                            style={{ opacity: camada.visivel ? 1 : 0.35 }}
                          />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-xs font-medium">{camada.nome}</span>
                          <span className="block truncate text-[11px] text-muted-foreground">
                            {ROTULO_PAPEL_IMAGEM[camada.papel]}
                            {camada.papel === 'CANTO' ? ` · ${ROTULO_CANTO[camada.canto]}` : ''}
                            {camada.papel === 'BORDA' ? ` · ${ROTULO_LADO[camada.lado]}` : ''}
                          </span>
                          <span className="block truncate text-[11px] text-muted-foreground">
                            {ROTULO_ESCOPO[camada.escopo].replace('Somente ', '')} ·{' '}
                            {Math.round(camada.larguraMm)}×{Math.round(camada.alturaMm)} mm
                          </span>
                        </span>
                      </button>

                      <div className="flex shrink-0 flex-col items-center justify-center gap-0.5">
                        <button
                          type="button"
                          title="Subir na pilha"
                          aria-label="Subir na pilha"
                          disabled={posicao === ordenadas.length - 1}
                          onClick={() => moverNaPilha(camada.id, 1)}
                          className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-30"
                        >
                          <ArrowUp className="size-3.5" />
                        </button>
                        <button
                          type="button"
                          title="Descer na pilha"
                          aria-label="Descer na pilha"
                          disabled={posicao === 0}
                          onClick={() => moverNaPilha(camada.id, -1)}
                          className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-30"
                        >
                          <ArrowDown className="size-3.5" />
                        </button>
                      </div>

                      <div className="flex shrink-0 flex-col items-center justify-center gap-0.5">
                        <button
                          type="button"
                          title={camada.visivel ? 'Ocultar camada' : 'Mostrar camada'}
                          aria-label={camada.visivel ? 'Ocultar camada' : 'Mostrar camada'}
                          onClick={() => alternarVisibilidade(camada.id)}
                          className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                        >
                          {camada.visivel ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
                        </button>
                        <button
                          type="button"
                          title="Excluir camada"
                          aria-label="Excluir camada"
                          onClick={() => removerCamada(camada.id)}
                          className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-destructive"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            <p className="text-xs leading-relaxed text-muted-foreground">
              A lista mostra as camadas da frente para trás. Use as setas para mudar o que fica por cima.
            </p>
          </section>

          {/* Editor da camada selecionada */}
          <section className="space-y-3">
            {camadaSelecionada ? (
              <CamadaEditor
                camada={camadaSelecionada}
                formato={config.formato}
                aoAtualizar={(alteracoes) => atualizarCamada(camadaSelecionada.id, alteracoes)}
                aoReplicar={(tipo) => replicar(camadaSelecionada.id, tipo)}
                aoDuplicar={() => duplicarCamada(camadaSelecionada.id)}
                aoRemover={() => removerCamada(camadaSelecionada.id)}
                aoTrazerParaFrente={() => trazerParaFrente(camadaSelecionada.id)}
              />
            ) : null}
          </section>
        </div>
      )}

      {/* Biblioteca de imagens já enviadas */}
      <Dialog open={mostrarBiblioteca} onOpenChange={setMostrarBiblioteca}>
        <DialogContent
          titulo="Minhas imagens"
          descricao="Clique em uma imagem para adicioná-la como nova camada. Clique com o botão de canto para usá-la nos quatro cantos."
        >
          {biblioteca.length === 0 ? (
            <p className="text-sm text-muted-foreground">Você ainda não enviou nenhuma imagem.</p>
          ) : (
            <ul className="grid max-h-[60vh] grid-cols-3 gap-3 overflow-y-auto sm:grid-cols-4">
              {biblioteca.map((arquivo) => (
                <li key={arquivo.id} className="space-y-1.5">
                  <button
                    type="button"
                    onClick={() => void adicionarCamada(arquivo.url, arquivo.nome)}
                    className="block aspect-square w-full overflow-hidden rounded-md border border-border transition-colors hover:border-primary"
                  >
                    <img src={urlDaImagem(arquivo.url)} alt={arquivo.nome} className="size-full object-cover" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void adicionarCamada(arquivo.url, arquivo.nome, 'CANTO')}
                    className="w-full rounded border border-border px-1.5 py-1 text-[10px] font-medium text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    <Plus className="mr-1 inline size-3" />
                    Quadro de cantos
                  </button>
                  <p className="truncate text-[10px] text-muted-foreground" title={arquivo.nome}>
                    {formatarBytes(arquivo.bytes)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {/* Biblioteca de ícones */}
      <Dialog open={mostrarIcones} onOpenChange={setMostrarIcones}>
        <DialogContent
          titulo="Biblioteca de ícones"
          descricao="Escolha um ícone para usar na agenda. Ele entra como uma camada: você posiciona, gira, escolhe o tamanho, a cor e em quais páginas aparece."
        >
          <SeletorDeIcones
            cor={paleta.destaque}
            aoEscolher={adicionarIcone}
            aoFechar={() => setMostrarIcones(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
